import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { ALL_MODULES, ROLES } from '../../utils/navigation';
import { useAuth } from '../../hooks/useAuth';

const BottomNavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const userRole = currentUser?.role?.toUpperCase() || '';
  const [isMoreMenuOpen, setMoreMenuOpen] = useState(false);

  const mainNav = [
    { name: 'Inicio', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Cuentas', path: '/inventario', icon: Users },
    { name: 'Finanzas', path: '/finanzas', icon: Wallet },
    { name: 'Supervisión', path: '/supervision', icon: ClipboardCheck },
  ];

  const visibleModules = useMemo(
    () => ALL_MODULES.filter(module => userRole === ROLES.ADMIN || module.allowedRoles?.includes(userRole)),
    [userRole],
  );

  const handleNavClick = path => {
    navigate(path);
    setMoreMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="md:hidden">
      <nav
        className="fixed bottom-0 left-0 right-0 w-full h-16 border-t border-[#2d3540] z-[9999] flex justify-around items-center pb-safe"
        style={{ backgroundColor: '#212529' }}
      >
        {mainNav.map(({ name, path, icon: Icon }) => {
          const isActive = location.pathname === path;

          return (
            <button
              key={path}
              onClick={() => handleNavClick(path)}
              className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-colors ${isActive ? 'text-emerald-500' : 'text-slate-400 hover:text-slate-200'}`}
            >
              <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
              <span className="text-[10px] font-medium">{name}</span>
            </button>
          );
        })}

        <button
          onClick={() => setMoreMenuOpen(!isMoreMenuOpen)}
          className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-colors ${isMoreMenuOpen ? 'text-emerald-500' : 'text-slate-400 hover:text-slate-200'}`}
          aria-expanded={isMoreMenuOpen}
          aria-controls="mobile-main-menu"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] font-medium">Menú</span>
        </button>
      </nav>

      {isMoreMenuOpen && (
        <>
          <button
            className="fixed inset-0 bg-black backdrop-blur-sm z-50 md:hidden"
            onClick={() => setMoreMenuOpen(false)}
            aria-label="Cerrar menú"
          />
          <section
            id="mobile-main-menu"
            className="fixed bottom-16 left-0 right-0 bg-[#161b22] rounded-t-2xl p-4 z-50 border-t border-slate-700 md:hidden max-h-[70vh] overflow-y-auto shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Menú Principal"
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className="text-base font-bold text-white">Menú Principal</h2>
              <button
                onClick={() => setMoreMenuOpen(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-1">
              {visibleModules.map(({ name, path, icon: Icon }) => {
                const isActive = location.pathname === path;

                return (
                  <button
                    key={path}
                    onClick={() => handleNavClick(path)}
                    className={`w-full flex items-center text-left p-3.5 rounded-xl transition-colors font-semibold text-sm ${isActive ? 'bg-emerald-600/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-300 hover:bg-slate-800'}`}
                  >
                    <Icon size={18} className="mr-3 text-emerald-500" />
                    <span>{name}</span>
                  </button>
                );
              })}
              <button
                onClick={handleLogout}
                className="w-full flex items-center text-left p-3.5 rounded-xl transition-colors font-semibold text-sm text-red-400 hover:bg-red-500/10"
              >
                <LogOut size={18} className="mr-3 text-red-500" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default BottomNavBar;

